/* eslint-disable react-native/no-unused-styles */
/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Image,
  ScrollView,
} from 'react-native';
import { useTheme, Button } from 'react-native-paper';
import QText from './QText';
import { useTranslation } from 'react-i18next';
import {
  showDateTime,
  showTimeZone,
  formatScheduleText,
  formatTimeDisplay,
} from '../utils';
import HumanQueueIcon from './Icons/HumanQueueIcon';
import SingleRightArrow from './Icons/SingleRightArrow';
import BatchQueueIcon from './Icons/BatchQueueIcon';
import AppointmentQueueIcon from './Icons/AppointmentQueueIcon';
import RefreshIcon from './Icons/RefreshIcon';
import CalendarIcon from './Icons/CalendarIcon';
// import AutoEnrollmentScheduleModal from './AutoEnrollmentScheduleModal';
import moment from 'moment';

const UserQueueCard = ({ data, onCardClick, navigation }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const item = data.item;

  const ordinalSuffix = (i) => {
    var j = i % 10,
      k = i % 100;
    if (j == 1 && k != 11) {
      return i + 'st';
    }
    if (j == 2 && k != 12) {
      return i + 'nd';
    }
    if (j == 3 && k != 13) {
      return i + 'rd';
    }
    return i + 'th';
  };

  const formatFutureDate = (dateString) => {
    const date = moment(dateString);
    const today = moment();
    const tomorrow = moment().add(1, 'day');

    if (date.isSame(today, 'day')) {
      return t('today');
    } else if (date.isSame(tomorrow, 'day')) {
      return t('tomorrow');
    } else if (date.diff(today, 'days') <= 7) {
      return date.format('ddd, MMM D');
    } else {
      return date.format('MMM D, YYYY');
    }
  };

  const renderFutureInstancesPreview = () => {
    if (!item?.future_instances || item.future_instances.length === 0)
      return null;

    // Show only 5 instances, then show +X for remaining
    const previewInstances = item.future_instances.slice(0, 5);
    const remainingCount = item.no_of_enrollments_left || 0;

    return (
      <View style={styles.futureInstancesContainer}>
        <View style={styles.futureInstancesHeader}>
          <View style={styles.futureInstancesTitleRow}>
            <CalendarIcon
              width={14}
              height={14}
              color={theme.colors.onSurfaceVariant}
            />
            <QText style={styles.futureInstancesTitle}>
              {t('upcoming_bookings')}
            </QText>
            <View style={styles.countBadge}>
              <QText style={styles.countBadgeText}>
                {item.future_instances.length +
                  (item.no_of_enrollments_left || 0)}
              </QText>
            </View>
          </View>
        </View>

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.futureInstancesScroll}
          contentContainerStyle={styles.futureInstancesScrollContent}
        >
          {previewInstances.map((instance, index) => (
            <View
              key={instance.id}
              style={[
                styles.futureInstanceCard,
                instance.status === 'Skipped' && styles.skippedInstanceCard,
              ]}
            >
              {instance.status === 'Skipped' && (
                <View style={styles.skippedIndicator}>
                  <QText style={styles.skippedText}>SKIPPED</QText>
                </View>
              )}
              <QText
                style={[
                  styles.futureInstanceDate,
                  instance.status === 'Skipped' && styles.skippedTextColor,
                ]}
              >
                {formatFutureDate(instance.booking_datetime)}
              </QText>
              <QText
                style={[
                  styles.futureInstanceTime,
                  instance.status === 'Skipped' && styles.skippedTextColor,
                ]}
              >
                {moment(instance.booking_datetime).format('h:mm A')}
              </QText>
              {instance.batch_name && (
                <QText
                  style={[
                    styles.futureInstanceBatch,
                    instance.status === 'Skipped' && styles.skippedTextColor,
                  ]}
                >
                  {instance.batch_name}
                </QText>
              )}
            </View>
          ))}

          {remainingCount > 0 && (
            <View style={styles.moreInstancesCard}>
              <QText style={styles.moreInstancesText}>+{remainingCount}</QText>
            </View>
          )}
        </ScrollView>
      </View>
    );
  };

  const renderIcon = () => {
    // If queue image URL or business profile image URL exists, show the image (prefer queue image)
    if (item?.image_url || item?.business_profile_image_url) {
      return (
        <View style={styles.businessProfileIconView}>
          <Image
            source={{ uri: item.image_url || item.business_profile_image_url }}
            style={styles.businessProfileImage}
            resizeMode="cover"
            backgroundColor="transparent"
          />
        </View>
      );
    }

    // Fallback to original icons
    if (item?.queue_type === 'Batch') {
      return (
        <View style={styles.batchIconView}>
          <BatchQueueIcon color="#fff" />
        </View>
      );
    } else if (item?.queue_type === 'Appointment') {
      return (
        <View style={styles.appointmentIconView}>
          <AppointmentQueueIcon color="#fff" />
        </View>
      );
    } else {
      return (
        <View style={styles.iconView}>
          <HumanQueueIcon color="#fff" />
        </View>
      );
    }
  };

  return (
    <>
      <TouchableOpacity
        style={[
          styles.container,
          item?.is_daily_auto_enrollment_enabled &&
            styles.autoEnrollmentContainer,
        ]}
        activeOpacity={0.8}
        onPress={() => onCardClick(item)}
      >
        <View style={styles.cardContent}>
          <View style={styles.mainContent}>
            <View style={styles.inlineBlock}>
              {renderIcon()}

              <View style={styles.textView}>
                <View style={styles.nameRow}>
                  <QText style={styles.shopName}>
                    {t(`${item.queue_name}`)}
                  </QText>
                  {item?.is_daily_auto_enrollment_enabled && (
                    <TouchableOpacity
                      style={styles.scheduleButton}
                      onPress={(e) => {
                        e.stopPropagation();
                        navigation?.navigate('AutoEnrollmentScheduleScreen', {
                          queueUserId: item.id,
                          queueName: item.queue_name,
                        });
                      }}
                    >
                      <QText style={styles.scheduleButtonText}>
                        {t('view_schedule')}
                      </QText>
                    </TouchableOpacity>
                  )}
                </View>

                <View style={styles.statusRow}>
                  <View style={styles.statusContainer}>
                    <QText style={styles.statusLabel}>{t('Status')}:</QText>
                    <QText
                      style={[
                        styles.statusValue,
                        {
                          color:
                            item.status === 'Confirmed'
                              ? theme.colors.primary
                              : theme.colors.secondaryText,
                        },
                      ]}
                    >
                      {item.status}
                    </QText>
                  </View>
                </View>

                <View style={styles.detailsRow}>
                  {item?.queue_type === 'Batch' ? (
                    <View style={styles.detailItem}>
                      <QText style={styles.detailLabel}>{t('batch')}:</QText>
                      <QText style={styles.detailValue}>
                        {item?.batch_name}
                      </QText>
                    </View>
                  ) : null}

                  <View style={styles.detailItem}>
                    <QText style={styles.detailLabel}>
                      {t('token_number')}:
                    </QText>
                    <QText style={styles.detailValue}>
                      {item.token_number}
                    </QText>
                  </View>

                  {item?.party_size && (
                    <View style={styles.detailItem}>
                      <QText style={styles.detailLabel}>
                        {t('group_size')}:
                      </QText>
                      <QText style={styles.detailValue}>
                        {item.party_size}
                      </QText>
                    </View>
                  )}
                </View>

                <View style={styles.dateTimeRow}>
                  <QText style={styles.dateTimeLabel}>
                    {item?.queue_type === 'Appointment'
                      ? t('booking_date')
                      : t('date_and_time')}
                    :
                  </QText>
                  <QText style={styles.dateTimeValue}>
                    {item?.queue_type === 'Appointment'
                      ? showDateTime(item.booking_datetime)
                      : showDateTime(item.estimated_serving_start_datetime)}
                    {showTimeZone(item?.queue_timezone)
                      ? ' ' + showTimeZone(item?.queue_timezone)
                      : ''}
                  </QText>
                </View>

                {/* Schedule Information - Same as QueueCard */}
                {item?.schedule?.weekdays && (
                  <View style={styles.scheduleSection}>
                    <View style={styles.scheduleInfoContainer}>
                      <QText style={styles.scheduleLabel}>SCHEDULE:</QText>
                      <QText style={styles.scheduleValue}>
                        {formatScheduleText(item)}
                      </QText>
                    </View>
                  </View>
                )}
              </View>
            </View>
          </View>

          <View style={styles.rightSection}>
            <SingleRightArrow color={theme.colors.text} />
          </View>
        </View>

        {/* Future Instances Preview for Auto-Enrolled Queues */}
        {item?.is_daily_auto_enrollment_enabled &&
          renderFutureInstancesPreview()}
      </TouchableOpacity>
    </>
  );
};

const createStyles = (theme) =>
  StyleSheet.create({
    container: {
      width: '100%',
      borderRadius: 16,
      backgroundColor: theme.colors.onBackgroundDark,
      padding: 20,
      marginBottom: 16,
      justifyContent: 'center',
      shadowColor: theme.colors.primary,
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.12,
      shadowRadius: 12,
      elevation: 4,
    },
    autoEnrollmentContainer: {
      backgroundColor: theme.colors.onBackgroundDark,
      shadowOpacity: 0.15,
      shadowRadius: 16,
      elevation: 6,
    },
    cardContent: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    mainContent: {
      flex: 1,
      marginRight: 12,
    },
    inlineBlock: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    iconView: {
      width: 52,
      height: 52,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.humanColor,
      borderRadius: 10,
    },
    batchIconView: {
      width: 52,
      height: 52,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.batchColor,
      borderRadius: 10,
    },
    appointmentIconView: {
      width: 52,
      height: 52,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.appointmentColor,
      borderRadius: 10,
    },
    textView: {
      flex: 1,
      paddingLeft: 12,
    },
    shopName: {
      fontSize: 18,
      color: theme.colors.secondaryText,
      fontWeight: 700,
      textTransform: 'capitalize',
      marginBottom: 4,
    },
    nameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 6,
    },
    scheduleButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 6,
      alignSelf: 'flex-start',
      flexShrink: 0,
      shadowColor: theme.colors.primary,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.2,
      shadowRadius: 4,
      elevation: 2,
    },
    scheduleButtonText: {
      fontSize: 12,
      color: theme.colors.onPrimary,
      fontWeight: '600',
      textAlign: 'center',
    },
    statusRow: {
      marginBottom: 6,
    },
    statusContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    statusLabel: {
      fontSize: 14,
      color: theme.colors.onSurfaceVariant,
      fontWeight: 500,
    },
    statusValue: {
      fontSize: 14,
      fontWeight: 600,
    },
    detailsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      marginBottom: 6,
    },
    detailItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    detailLabel: {
      fontSize: 13,
      color: theme.colors.onSurfaceVariant,
      fontWeight: 500,
    },
    detailValue: {
      fontSize: 13,
      color: theme.colors.secondaryText,
      fontWeight: 600,
    },
    dateTimeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    dateTimeLabel: {
      fontSize: 13,
      color: theme.colors.onSurfaceVariant,
      fontWeight: 500,
    },
    dateTimeValue: {
      fontSize: 13,
      color: theme.colors.secondaryText,
      fontWeight: 600,
    },
    // Schedule section styles
    scheduleSection: {
      marginTop: 12,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: 'rgba(148, 163, 184, 0.15)',
      gap: 8,
    },
    scheduleInfoContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    scheduleLabel: {
      fontSize: 11,
      color: theme.colors.placeHolderText,
      textTransform: 'uppercase',
      fontWeight: '600',
      letterSpacing: 0.3,
    },
    scheduleValue: {
      fontSize: 12,
      color: theme.colors.secondaryText,
      fontWeight: '500',
      flex: 1,
    },
    rightSection: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      width: 28,
    },
    // Future Instances Styles
    futureInstancesContainer: {
      marginTop: 16,
      paddingTop: 16,
    },
    futureInstancesHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 10,
    },
    futureInstancesTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    futureInstancesTitle: {
      fontSize: 16,
      color: theme.colors.onSurface,
      fontWeight: 700,
    },
    countBadge: {
      backgroundColor: theme.colors.primary,
      borderRadius: 12,
      paddingHorizontal: 8,
      paddingVertical: 3,
      marginLeft: 6,
    },
    countBadgeText: {
      fontSize: 11,
      color: theme.colors.onPrimary,
      fontWeight: 700,
    },
    futureInstancesScroll: {
      flexGrow: 0,
    },
    futureInstancesScrollContent: {
      paddingRight: 4,
    },
    futureInstanceCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      padding: 12,
      marginRight: 8,
      minWidth: 75,
      alignItems: 'center',
      borderWidth: 1,
      borderColor: theme.colors.outline,
      shadowColor: theme.colors.primary,
      shadowOffset: {
        width: 0,
        height: 3,
      },
      shadowOpacity: 0.15,
      shadowRadius: 8,
      elevation: 4,
    },
    futureInstanceDate: {
      fontSize: 12,
      color: theme.colors.onSurface,
      fontWeight: 700,
      textAlign: 'center',
      marginBottom: 4,
    },
    futureInstanceTime: {
      fontSize: 11,
      color: theme.colors.onSurface,
      fontWeight: 600,
      textAlign: 'center',
      marginBottom: 3,
    },
    futureInstanceBatch: {
      fontSize: 10,
      color: theme.colors.primary,
      fontWeight: 700,
      textAlign: 'center',
    },
    moreInstancesCard: {
      backgroundColor: theme.colors.primary,
      borderRadius: 12,
      padding: 12,
      minWidth: 55,
      height: 55,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: theme.colors.primary,
      shadowColor: theme.colors.primary,
      shadowOffset: {
        width: 0,
        height: 3,
      },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 5,
    },
    moreInstancesText: {
      fontSize: 13,
      color: theme.colors.onPrimary,
      fontWeight: 800,
      textAlign: 'center',
    },
    // Skipped booking styles
    skippedInstanceCard: {
      backgroundColor: 'transparent',
      borderColor: theme.colors.error,
      borderWidth: 2,
    },
    skippedIndicator: {
      position: 'absolute',
      top: -2,
      right: -2,
      backgroundColor: theme.colors.error,
      borderRadius: 8,
      paddingHorizontal: 4,
      paddingVertical: 2,
      zIndex: 1,
    },
    skippedText: {
      fontSize: 8,
      color: theme.colors.onError,
      fontWeight: 800,
      textAlign: 'center',
    },
    skippedTextColor: {
      color: theme.colors.onError,
      opacity: 0.7,
    },
    businessProfileImage: {
      width: '100%',
      height: '100%',
      borderRadius: 10,
    },
    businessProfileIconView: {
      width: 52,
      height: 52,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 10,
      overflow: 'hidden',
    },
  });
export default UserQueueCard;
